import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../systems/auth-context';
import {
  AssetSubdir,
  assetUrl,
  deleteAsset,
  listAssets,
  parseAssetPath,
  uploadAsset,
} from './scenario-api';
import { AssetReplaceModal } from './AssetReplaceModal';
import { AssetPreviewModal } from './AssetPreviewModal';
import { StudioLoader } from './StudioLoader';
import styles from './scenario-studio.module.css';

interface AssetManagerProps {
  scenarioId: string;
}

interface PendingReplace {
  path: string;
  file: File;
  previewUrl: string;
}

export const AssetManager: React.FC<AssetManagerProps> = ({ scenarioId }) => {
  const { user } = useAuth();
  const [assets, setAssets] = useState<string[]>([]);
  const [subdir, setSubdir] = useState<AssetSubdir>('backgrounds');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);
  const [pendingReplace, setPendingReplace] = useState<PendingReplace | null>(null);
  const [replaceTargetPath, setReplaceTargetPath] = useState<string | null>(null);
  const [cacheBust, setCacheBust] = useState(0);
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const list = await listAssets(scenarioId);
      setAssets(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(console.error);
  }, [scenarioId, user]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploading(true);
    try {
      await uploadAsset(scenarioId, file, subdir);
      setCacheBust((v) => v + 1);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const startReplace = (path: string) => {
    setReplaceTargetPath(path);
    replaceInputRef.current?.click();
  };

  const handleReplaceFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !replaceTargetPath) return;
    const previewUrl = URL.createObjectURL(file);
    setPendingReplace({ path: replaceTargetPath, file, previewUrl });
    setReplaceTargetPath(null);
  };

  const cancelReplace = () => {
    if (pendingReplace) URL.revokeObjectURL(pendingReplace.previewUrl);
    setPendingReplace(null);
  };

  const confirmReplace = async () => {
    if (!pendingReplace || !user) return;
    const parsed = parseAssetPath(pendingReplace.path);
    if (!parsed) {
      alert('Chemin asset invalide');
      return;
    }
    setReplacing(true);
    try {
      await uploadAsset(
        scenarioId,
        pendingReplace.file,
        parsed.subdir,
        parsed.relPath
      );
      setCacheBust((v) => v + 1);
      await load();
      cancelReplace();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Remplacement échoué');
    } finally {
      setReplacing(false);
    }
  };

  const handleDelete = async (path: string) => {
    if (!user || !window.confirm(`Supprimer ${path} ?`)) return;
    setDeletingPath(path);
    try {
      await deleteAsset(scenarioId, path);
      await load();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingPath(null);
    }
  };

  const filtered = assets.filter((a) => a.includes(`assets/${subdir}`));
  const busy = loading || uploading || replacing || deletingPath !== null;

  const imgSrc = (path: string) => {
    const url = assetUrl(scenarioId, path);
    return cacheBust ? `${url}?t=${cacheBust}` : url;
  };

  return (
    <div className={styles.panel}>
      <h3>Gestion des assets</h3>
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleReplaceFileSelected}
        disabled={busy}
      />
      <div className={styles.row}>
        <select
          className={styles.select}
          value={subdir}
          onChange={(e) => setSubdir(e.target.value as AssetSubdir)}
          disabled={busy}
        >
          <option value="backgrounds">backgrounds</option>
          <option value="characters">characters</option>
          <option value="elements">elements</option>
          <option value="items">items</option>
        </select>
        <label className={`${styles.btnSecondary} ${uploading ? styles.btnLoading : ''}`}>
          {uploading ? (
            <>
              <span className={styles.spinner} /> Upload…
            </>
          ) : (
            'Uploader'
          )}
          <input type="file" accept="image/*" hidden onChange={handleUpload} disabled={busy} />
        </label>
        <button type="button" className={styles.btnSecondary} onClick={() => load()} disabled={busy}>
          {loading ? 'Rafraîchissement…' : 'Rafraîchir'}
        </button>
      </div>
      <div className={styles.assetGrid} style={{ position: 'relative', minHeight: 160 }}>
        {loading && <StudioLoader label="Chargement des assets…" variant="overlay" />}
        {!loading && filtered.map((path) => (
          <div key={path} className={styles.assetCard}>
            <button
              type="button"
              className={styles.assetCardThumb}
              onClick={() => setPreviewPath(path)}
              title="Agrandir"
            >
              <img src={imgSrc(path)} alt="" />
            </button>
            <span>{path}</span>
            <div className={styles.assetCardActions}>
              <button
                type="button"
                className={`${styles.btnSecondary} ${styles.assetIconBtn}`}
                onClick={() => startReplace(path)}
                disabled={busy}
                title="Remplacer"
                aria-label="Remplacer"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" />
                </svg>
              </button>
              <button
                type="button"
                className={`${styles.btnDanger} ${styles.assetIconBtn}`}
                onClick={() => handleDelete(path)}
                disabled={deletingPath !== null}
                title="Supprimer"
                aria-label="Supprimer"
              >
                {deletingPath === path ? (
                  <span className={styles.spinner} />
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <p className={styles.hint}>Aucun asset dans ce dossier.</p>
        )}
      </div>
      {pendingReplace && (
        <AssetReplaceModal
          scenarioId={scenarioId}
          assetPath={pendingReplace.path}
          previewUrl={pendingReplace.previewUrl}
          fileName={pendingReplace.file.name}
          confirming={replacing}
          onConfirm={confirmReplace}
          onCancel={cancelReplace}
        />
      )}
      {previewPath && (
        <AssetPreviewModal
          src={imgSrc(previewPath)}
          path={previewPath}
          onClose={() => setPreviewPath(null)}
        />
      )}
    </div>
  );
};
